import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_details: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.project_details) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({ name: '', phone: '', email: '', project_details: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="section-padding bg-[#2A2A2A]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="overline text-[#B5703A] block mb-4">Get in Touch</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#F5F0E8] tracking-tight mb-6">
              Let's Create<br />
              Something Meaningful
            </h2>
            <p className="text-[#C4B6A4] font-light max-w-md leading-relaxed mb-8">
              Tell us about your space. We'll take it from there.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <div>
                <span className="text-sm text-[#B5703A] block mb-1">Phone</span>
                <a href="tel:9972765135" className="text-[#F5F0E8] font-light hover:text-[#B5703A] transition-colors">
                  +91 9972765135
                </a>
                <span className="text-[#C4B6A4] mx-2">|</span>
                <a href="tel:8296031515" className="text-[#F5F0E8] font-light hover:text-[#B5703A] transition-colors">
                  +91 8296031515
                </a>
              </div>
              <div>
                <span className="text-sm text-[#B5703A] block mb-1">Email</span>
                <a href="mailto:connect@livingcraftsmen.com" className="text-[#F5F0E8] font-light hover:text-[#B5703A] transition-colors">
                  connect@livingcraftsmen.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-4 bg-[#3D3D3D] border border-[#4A4A4A] text-[#F5F0E8] placeholder-[#8A8A7A] focus:outline-none focus:border-[#B5703A] transition-colors"
                data-testid="contact-name-input"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full p-4 bg-[#3D3D3D] border border-[#4A4A4A] text-[#F5F0E8] placeholder-[#8A8A7A] focus:outline-none focus:border-[#B5703A] transition-colors"
                data-testid="contact-phone-input"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full p-4 bg-[#3D3D3D] border border-[#4A4A4A] text-[#F5F0E8] placeholder-[#8A8A7A] focus:outline-none focus:border-[#B5703A] transition-colors"
                data-testid="contact-email-input"
              />
              <textarea
                placeholder="Tell us about your space..."
                rows={4}
                value={formData.project_details}
                onChange={(e) => setFormData(prev => ({ ...prev, project_details: e.target.value }))}
                className="w-full p-4 bg-[#3D3D3D] border border-[#4A4A4A] text-[#F5F0E8] placeholder-[#8A8A7A] focus:outline-none focus:border-[#B5703A] transition-colors resize-none"
                data-testid="contact-details-input"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full p-4 bg-[#B5703A] text-white font-medium uppercase tracking-wider hover:bg-[#9A5F30] transition-colors disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Schedule a Consultation'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
